import React, { useRef, useState, useEffect } from 'react';
import { Button, Box, Typography, Stepper, Step, StepLabel, Card, Alert } from '@mui/material';
import AgoraRTC from 'agora-rtc-sdk-ng';

const ExamSetup = ({ handleNext, localTracksRef, setProctoringStatus, cameraPreviewRef, isMobile }) => {
  // Steps for both mobile and desktop (screen share removed)
  const steps = ['Camera & Mic', 'Verification'];

  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState('');

  // Helper: stop and clear any previously stored Agora tracks
  const stopAndClearTrackObj = (trackObj) => {
    try {
      if (!trackObj) return;
      if (trackObj.stop) trackObj.stop();
      if (trackObj.close) trackObj.close();
    } catch (e) {
      console.warn('Error stopping track:', e);
    }
  };

  // Request media permissions - adapts for mobile and desktop
  const requestMediaPermissions = async () => {
    setError('');
    try {
      // If mobile: use getUserMedia to ensure prompt is triggered by button click
      if (isMobile) {
        // Stop previous local tracks if any
        stopAndClearTrackObj(localTracksRef.current.video);
        stopAndClearTrackObj(localTracksRef.current.audio);

        const wantAudio = true; // change to false if you want to avoid mic on mobile
        const constraints = {
          video: { facingMode: 'user' },
          audio: wantAudio,
        };

        // This call must be from user click/tap to show permission prompt
        const stream = await navigator.mediaDevices.getUserMedia(constraints);

        // Present preview using the MediaStream
        if (cameraPreviewRef && cameraPreviewRef.current) {
          cameraPreviewRef.current.srcObject = stream;
          // playsInline & muted help mobile autoplay policies
          try {
            await cameraPreviewRef.current.play().catch(() => {});
          } catch (playErr) {
            console.warn('Preview play error:', playErr);
          }
        }

        // Create Agora custom tracks from the MediaStream tracks
        const videoTrack = stream.getVideoTracks()[0];
        const audioTrack = stream.getAudioTracks()[0];

        // Remove previous refs
        localTracksRef.current.video = null;
        localTracksRef.current.audio = null;

        if (videoTrack) {
          localTracksRef.current.video = AgoraRTC.createCustomVideoTrack({ mediaStreamTrack: videoTrack });
        }
        if (audioTrack) {
          localTracksRef.current.audio = AgoraRTC.createCustomAudioTrack({ mediaStreamTrack: audioTrack });
        }

        // Update proctoring status
        setProctoringStatus(prev => ({
          ...prev,
          camera: !!localTracksRef.current.video,
          microphone: !!localTracksRef.current.audio,
        }));

        // Move to verification step
        setActiveStep(1);
        setError('');
        return;
      }

      // Desktop flow: use Agora SDK helpers to create camera & mic tracks
      // Stop previous tracks if present
      stopAndClearTrackObj(localTracksRef.current.video);
      stopAndClearTrackObj(localTracksRef.current.audio);

      localTracksRef.current.video = await AgoraRTC.createCameraVideoTrack();
      localTracksRef.current.audio = await AgoraRTC.createMicrophoneAudioTrack();

      // If preview element exists, Agora camera track provides a .play() method
      if (cameraPreviewRef && cameraPreviewRef.current && localTracksRef.current.video && localTracksRef.current.video.play) {
        try {
          // Agora track.play API accepts element
          localTracksRef.current.video.play(cameraPreviewRef.current);
        } catch (err) {
          // fallback: attach via srcObject if possible
          console.warn('Agora play fallback:', err);
          try {
            const stream = new MediaStream();
            const mediaVideoTrack = localTracksRef.current.video.getMediaStreamTrack ? localTracksRef.current.video.getMediaStreamTrack() : null;
            if (mediaVideoTrack) {
              stream.addTrack(mediaVideoTrack);
              cameraPreviewRef.current.srcObject = stream;
              await cameraPreviewRef.current.play().catch(() => {});
            }
          } catch (e) {
            console.warn('Preview fallback failed:', e);
          }
        }
      }

      setProctoringStatus(prev => ({
        ...prev,
        camera: !!localTracksRef.current.video,
        microphone: !!localTracksRef.current.audio,
      }));

      // Move to verification step
      setActiveStep(1);
      setError('');
    } catch (err) {
      console.error('Media access error:', err);
      setError('Failed to access camera/microphone. Please allow permissions and refresh.');
      setProctoringStatus(prev => ({ ...prev, camera: false, microphone: false }));
      // clear any partially created tracks
      try {
        stopAndClearTrackObj(localTracksRef.current.video);
        stopAndClearTrackObj(localTracksRef.current.audio);
      } finally {
        localTracksRef.current.video = null;
        localTracksRef.current.audio = null;
      }
    }
  };

  const handleComplete = () => {
    // Check if camera and microphone are active
    const cameraOk = !!localTracksRef.current.video;
    const micOk = !!localTracksRef.current.audio;

    if (!cameraOk || !micOk) {
      setError('Camera and microphone must be active to continue.');
      return;
    }
    handleNext();
  };

  // Reset to first step if device type changes
  useEffect(() => {
    setActiveStep(0);
  }, [isMobile]);

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Card sx={{ p: 3 }}>
        {/* CAMERA & MIC */}
        {activeStep === 0 && (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom>
              Allow Camera & Microphone
            </Typography>
            <Typography sx={{ mb: 3 }}>
              Please allow access to your camera and microphone for proctoring.
            </Typography>

            <Button
              variant="contained"
              onClick={requestMediaPermissions}
              size="large"
            >
              Allow Camera & Mic
            </Button>

            <Box sx={{ mt: 3 }}>
              <video
                ref={cameraPreviewRef}
                autoPlay
                playsInline
                muted
                style={{
                  width: '100%',
                  maxWidth: 400,
                  border: '1px solid #ddd',
                  display: (localTracksRef.current.video) ? 'block' : 'none',
                  borderRadius: '4px'
                }}
              />
            </Box>
          </Box>
        )}

        {/* VERIFICATION */}
        {activeStep === 1 && (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom>
              Verification Complete
            </Typography>
            <Typography sx={{ mb: 3 }}>
              Your device setup is complete and ready for the exam.
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Typography>
                <span style={{ color: localTracksRef.current.video ? 'green' : 'red' }}>
                  ● Camera: {localTracksRef.current.video ? 'Active' : 'Inactive'}
                </span>
              </Typography>
              <Typography>
                <span style={{ color: localTracksRef.current.audio ? 'green' : 'red' }}>
                  ● Microphone: {localTracksRef.current.audio ? 'Active' : 'Inactive'}
                </span>
              </Typography>
            </Box>

            <Button
              variant="contained"
              onClick={handleComplete}
              size="large"
              disabled={!localTracksRef.current.video || !localTracksRef.current.audio}
            >
              Continue to Instructions
            </Button>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Card>
    </Box>
  );
};

export default ExamSetup;